import { Component, OnInit, signal, inject, ChangeDetectionStrategy } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { TranslocoPipe, TranslocoService } from "@jsverse/transloco";
import { first } from "rxjs/operators";
import { LorenBaseFeature } from "../../../../shared/base-components/loren-base-feature/loren-base-feature";
import { LorenBreakpointObserverService } from "../../../../shared/services/loren-breakpoint-observer-service/loren-breakpoint-observer.service";
import { LorenNavService } from "../../../../shared/modules/loren-nav/components/loren-nav/services/loren-nav-service/loren-nav.service";
import { LorenAuthenticationService } from "../../../../shared/services/loren-authentication-service/loren-authentication.service";
import { LorenUserInfo } from "../../../../shared/interfaces/loren-user-info.interface";
import { LorenHttpResponse } from "../../../../shared/interfaces/loren-http-response.interface";
import { LorenUsersEndpointsService } from "../../../../shared/services/loren-users-endpoints-service/loren-users-endpoints.service";
import { LorenHttpErrorTranslationService } from "../../../../shared/services/loren-http-error-translation-service/loren-http-error-translation.service";
import { LorenHttpResponseError } from "../../../../shared/interfaces/loren-http-response-error.interface";
import { LorenSnackBarService } from "../../../../shared/services/loren-snack-bar-service/loren-snack-bar.service";
import { LorenFeatureToggleService } from "../../../../shared/services/loren-feature-toggle-service/loren-feature-toggle.service";
import { LorenFeature } from "../../../../shared/enums/loren-feature.enum";
import { LorenPrivilegeEntityType } from "../../../loren-roles/interfaces/loren-role.interface";
import { LorenPageContentComponent } from "../../../../shared/modules/loren-page-content/loren-page-content.component";
import { LorenUserProfileDetailsComponent } from "./components/loren-user-profile-details/loren-user-profile-details.component";
import { LorenUserProfilePasswordComponent } from "./components/loren-user-profile-password/loren-user-profile-password.component";
import { LorenUserProfilePictureComponent } from "./components/loren-user-profile-picture/loren-user-profile-picture.component";
import { MatCardModule } from "@angular/material/card";
import { LorenKeyValueComponent } from "../../../../shared/modules/loren-key-value-control/loren-key-value/loren-key-value.component";
import { LorenSharedDirectivesModule } from "../../../../shared/directives/loren-shared-directives.module";
import { LorenReportsService } from "../../../loren-assets-reports/services/loren-reports-service/loren-reports.service";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";

@Component({
  imports: [
    MatCardModule,
    TranslocoPipe,
    LorenPageContentComponent,
    LorenUserProfileDetailsComponent,
    LorenUserProfilePasswordComponent,
    LorenUserProfilePictureComponent,
    LorenKeyValueComponent,
    LorenSharedDirectivesModule,
  ],
  selector: "loren-loren-user-profile",
  standalone: true,
  styleUrls: ["./loren-user-profile.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: "./loren-user-profile.component.html",
})
export class LorenUserProfileComponent
  extends LorenBaseFeature
  implements OnInit
{
  protected breakpointObserverService: LorenBreakpointObserverService;
  protected lorenNavService: LorenNavService;
  protected lorenAuthenticationService = inject(LorenAuthenticationService);
  protected lorenUsersEndpointsService = inject(LorenUsersEndpointsService);
  protected http = inject(HttpClient);
  private lorenSnackBarService = inject(LorenSnackBarService);
  private translateService = inject(TranslocoService);
  private lorenHttpErrorTranslationServiceService = inject(LorenHttpErrorTranslationService);
  private featureToggleService = inject(LorenFeatureToggleService);
  private lorenReportsService = inject(LorenReportsService);

  constructor() {
    const breakpointObserverService = inject(LorenBreakpointObserverService);
    const lorenNavService = inject(LorenNavService);

    super();
  
    this.breakpointObserverService = breakpointObserverService;
    this.lorenNavService = lorenNavService;
  }

  readonly privileges = LorenPrivilegeEntityType;

  userInfo = signal<LorenUserInfo>(null);
  smsNotificationsEnabled = signal<boolean>(false);
  smsNumber = signal<number>(null);

  ngOnInit(): void {
    this.lorenAuthenticationService
        .getUserInfoSubscription()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((userInfo) => this.userInfo.set(userInfo));

    this.featureToggleService
        .userHasFeature(LorenFeature.SMS_NOTIFICATION)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((res) => {
          if (res) {
            this.lorenReportsService
                .getSMSNumber()
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe((res: { smsCount: number }) =>
                    this.smsNumber.set(res.smsCount)
                );
          }
          this.smsNotificationsEnabled.set(res);
        });
  }

  updateUserInfo(updatedUserInfo: LorenUserInfo) {
    this.http
      .patch(
        this.lorenUsersEndpointsService.userInfo(),
        updatedUserInfo
      )
      .subscribe({
        next: (res) => {
          const response = res as LorenHttpResponse;
          if (response.success) {
            this.lorenAuthenticationService.updateUserInfo();
            this.lorenSnackBarService.showSuccessMessage();
          }
        },
        error: (error) => {
          const responseErrors = error.error.errors as LorenHttpResponseError[];
          const errors =
            this.lorenHttpErrorTranslationServiceService.getTranslatedErrorsFromResponse(
              responseErrors
            );
          this.translateService
            .selectTranslate("errors.user_details_update_failure", {
              value: errors.join(","),
            })
            .pipe(first())
            .subscribe((translation) => {
              this.lorenSnackBarService.showErrorMessage(translation);
            });
        },
      });
  }
}
